import {
  Flex,
  FormControl,
  FormLabel,
  Text,
  Select,
  Input,
  IconButton,
  Box,
  FormErrorMessage,
} from '@chakra-ui/react';
import { RefObject } from 'react';
import AddonInput from '../AddonInput';
import BuilderSwitch from '../BuilderSwitch';
import { useAppDispatch, useAppSelector } from '../../../../store/redux/hooks';
import {
  setFormFieldItemProp,
  setFormFieldOptionProp,
  addFormFieldOption,
} from '../../../../store/redux/slices/form.slice';
import { FormFieldState, FormState } from '../../../../store/types/form';
import AddMoreButton from '../AddMoreButton';
import { removeFormFieldOption } from '../../../../store/redux/slices/form.slice';
import { CustomBinIcon } from '../../../../theme/multicolor-icons/CustomBinIcon';
import FieldEditIndicator from '../FieldEditIndicator';
import { CustomErrorIcon } from '../../../../theme/icons/CustomErrorIcon';
import { getFormFieldError } from '../validation/validationUtils';
interface DropdownFormFieldRefs {
  idRef: RefObject<HTMLDivElement>;
  labelRef: RefObject<HTMLDivElement>;
  placeholderRef: RefObject<HTMLDivElement>;
  requiredRef: RefObject<HTMLDivElement>;
  getOptionRef: (optionId: string) => RefObject<HTMLDivElement>;
}

const DropdownFormField = ({
  fieldData,
  fieldIndex,
  templateFieldOptions,
  selectedByOtherFormFields,
  handleFocus,
  handleConnectToPassField,
  validateSection,
  form,
  markTouched,
  fieldRefs,
  activeFieldRef,
  isFieldBeingEdited,
}: {
  fieldData: FormFieldState;
  fieldIndex: number;
  templateFieldOptions: { id: string; label: string }[];
  selectedByOtherFormFields: Set<string>;
  handleFocus: (path: string) => void;
  handleConnectToPassField: (value: string) => void;
  validateSection: (context?: object, validationContext?: object) => void;
  form: FormState;
  markTouched: (field: 'id' | 'label' | 'pattern' | 'message') => void;
  fieldRefs: DropdownFormFieldRefs;
  activeFieldRef: RefObject<HTMLDivElement> | null;
  isFieldBeingEdited: boolean;
}) => {
  const dispatch = useAppDispatch();
  const errors = useAppSelector((s) => s.templateErrors);
  const { idRef, labelRef, placeholderRef, requiredRef, getOptionRef } =
    fieldRefs;
  const getError = (path: string) => getFormFieldError(errors, path);

  return (
    <Flex direction="column" gap={4} mt={8}>
      <FieldEditIndicator
        fieldRef={idRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="115px"
      >
        <FormControl isInvalid={!!getError(`form.formFields.${fieldIndex}.id`)}>
          <FormLabel variant="templateBuilderLabel">
            Connect to pass field
          </FormLabel>
          <Text fontSize="12px" my={4}>
            <em>
              Select a field from your pass to link this form input to. The
              connected value will be passed directly to your pass template.
            </em>
          </Text>
          <Select
            value={fieldData.id}
            onChange={(e) => handleConnectToPassField(e.target.value)}
            onFocus={() => {
              handleFocus(`form.formFields.${fieldIndex}.id`);
              markTouched('id');
            }}
            placeholder="Select a field..."
          >
            {templateFieldOptions.map((tf) => (
              <option
                key={tf.id}
                value={tf.id}
                disabled={
                  tf.id !== 'none' && selectedByOtherFormFields.has(tf.id)
                }
              >
                {tf.id === 'none' ? tf.label : tf.id}
              </option>
            ))}
          </Select>
          {getError(`form.formFields.${fieldIndex}.id`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.id`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FieldEditIndicator
        fieldRef={labelRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="53px"
      >
        <FormControl
          isInvalid={!!getError(`form.formFields.${fieldIndex}.label`)}
        >
          <FormLabel variant="templateBuilderLabel">Heading</FormLabel>
          <AddonInput
            label="Label"
            placeholder="Enter a label"
            value={fieldData.label || ''}
            onChange={(value) => {
              markTouched('label');
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'label',
                  value: value as unknown as string,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, label: value as string } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            onFocus={() => {
              handleFocus(
                fieldData.id
                  ? `fields.${fieldData.id}.label`
                  : `form.formFields.${fieldIndex}.label`
              );
              markTouched('label');
            }}
            isRequired={!!fieldData.label}
          />
          {getError(`form.formFields.${fieldIndex}.label`) && (
            <FormErrorMessage
              pl={2}
              mt={2}
              display="flex"
              alignItems="center"
              gap={1.5}
            >
              <CustomErrorIcon />
              {getError(`form.formFields.${fieldIndex}.label`)}
            </FormErrorMessage>
          )}
        </FormControl>
      </FieldEditIndicator>
      <FormControl>
        <FormLabel variant="templateBuilderLabel">Drop down options</FormLabel>
        <FieldEditIndicator
          fieldRef={placeholderRef}
          isFieldBeingEdited={isFieldBeingEdited}
          activeFieldRef={activeFieldRef}
          left="-55px"
          top="25px"
        >
          <FormControl
            mb={4}
            isInvalid={!!getError(`form.formFields.${fieldIndex}.placeholder`)}
          >
            <AddonInput
              label="Placeholder"
              placeholder="Enter a placeholder"
              value={fieldData.placeholder ?? ''}
              onChange={(value) => {
                dispatch(
                  setFormFieldItemProp({
                    fieldId: fieldData._clientId ?? fieldData.id,
                    key: 'placeholder',
                    value: value as unknown as string,
                  })
                );
                const updatedFormFields = form.formFields.map((f, i) =>
                  i === fieldIndex ? { ...f, placeholder: value as string } : f
                );
                validateSection({ ...form, formFields: updatedFormFields });
              }}
              onFocus={() =>
                handleFocus(
                  fieldData.id
                    ? `fields.${fieldData.id}.placeholder`
                    : `form.formFields.${fieldIndex}.placeholder`
                )
              }
            />
            {getError(`form.formFields.${fieldIndex}.placeholder`) && (
              <FormErrorMessage
                pl={2}
                mt={2}
                display="flex"
                alignItems="center"
                gap={1.5}
              >
                <CustomErrorIcon />
                {getError(`form.formFields.${fieldIndex}.placeholder`)}
              </FormErrorMessage>
            )}
          </FormControl>
        </FieldEditIndicator>
        {(fieldData.options ?? []).map((option, optionIndex) => (
          <FieldEditIndicator
            key={option.id}
            fieldRef={getOptionRef(option.id)}
            isFieldBeingEdited={isFieldBeingEdited}
            activeFieldRef={activeFieldRef}
            left="-55px"
            top="26px"
          >
            <FormControl
              isInvalid={
                !!getError(
                  `form.formFields.${fieldIndex}.options.${optionIndex}.label`
                )
              }
            >
              <Flex align="center" mb={3}>
                <Input
                  value={option.label}
                  placeholder={`Option ${optionIndex + 1}`}
                  onChange={(e) => {
                    const newValue = e.target.value;
                    dispatch(
                      setFormFieldOptionProp({
                        fieldId: fieldData._clientId ?? fieldData.id,
                        optionId: option.id,
                        key: '_labelTouched',
                        value: true,
                      })
                    );
                    dispatch(
                      setFormFieldOptionProp({
                        fieldId: fieldData._clientId ?? fieldData.id,
                        optionId: option.id,
                        key: 'label',
                        value: newValue,
                      })
                    );
                    const updatedFormFields = form.formFields.map((f, i) =>
                      i === fieldIndex
                        ? {
                            ...f,
                            options: (f.options ?? []).map((opt) =>
                              opt.id === option.id
                                ? {
                                    ...opt,
                                    label: newValue,
                                    _labelTouched: true,
                                  }
                                : opt
                            ),
                          }
                        : f
                    );
                    validateSection({ ...form, formFields: updatedFormFields });
                  }}
                  onFocus={() => {
                    handleFocus(
                      `form.formFields.${fieldIndex}.options.${option.id}.label`
                    );
                    dispatch(
                      setFormFieldOptionProp({
                        fieldId: fieldData._clientId ?? fieldData.id,
                        optionId: option.id,
                        key: '_labelTouched',
                        value: true,
                      })
                    );
                  }}
                />
                <IconButton
                  ml={2}
                  aria-label="Remove option"
                  icon={<CustomBinIcon />}
                  isDisabled={(fieldData.options ?? []).length <= 1}
                  variant="ghost"
                  onClick={() => {
                    dispatch(
                      removeFormFieldOption({
                        fieldId: fieldData._clientId ?? fieldData.id,
                        optionId: option.id,
                      })
                    );
                    const updatedFormFields = form.formFields.map((f, i) =>
                      i === fieldIndex
                        ? {
                            ...f,
                            options: (f.options ?? []).filter(
                              (opt) => opt.id !== option.id
                            ),
                          }
                        : f
                    );
                    validateSection({ ...form, formFields: updatedFormFields });
                  }}
                />
              </Flex>
              {getError(
                `form.formFields.${fieldIndex}.options.${optionIndex}.label`
              ) && (
                <FormErrorMessage
                  pl={2}
                  my={2}
                  display="flex"
                  alignItems="center"
                  gap={1.5}
                >
                  <CustomErrorIcon />
                  {getError(
                    `form.formFields.${fieldIndex}.options.${optionIndex}.label`
                  )}
                </FormErrorMessage>
              )}
            </FormControl>
          </FieldEditIndicator>
        ))}
        <Box mt={2}>
          <AddMoreButton
            onClick={() => {
              dispatch(
                addFormFieldOption({
                  fieldId: fieldData._clientId ?? fieldData.id,
                })
              );
              const field = form.formFields[fieldIndex];
              const newOptions = [
                ...(field?.options ?? []),
                { id: 'new', label: '' },
              ];
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, options: newOptions } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
            text="Add option"
          />
        </Box>
      </FormControl>
      <FieldEditIndicator
        fieldRef={requiredRef}
        isFieldBeingEdited={isFieldBeingEdited}
        activeFieldRef={activeFieldRef}
        left="-55px"
        top="12px"
      >
        <Flex direction="row" width="100%" justifyContent="space-between">
          <Text>Require field?</Text>
          <BuilderSwitch
            isChecked={fieldData.required}
            onFocus={() =>
              handleFocus(`form.formFields.${fieldIndex}.required`)
            }
            onChange={(e) => {
              const checked = e.target.checked;
              dispatch(
                setFormFieldItemProp({
                  fieldId: fieldData._clientId ?? fieldData.id,
                  key: 'required',
                  value: checked,
                })
              );
              const updatedFormFields = form.formFields.map((f, i) =>
                i === fieldIndex ? { ...f, required: checked } : f
              );
              validateSection({ ...form, formFields: updatedFormFields });
            }}
          />
        </Flex>
      </FieldEditIndicator>
    </Flex>
  );
};

export default DropdownFormField;
